/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { Component } from "react";
import { ContextMenuItemContainer } from "containers/contextMenu/ContextMenuItemContainer";
import { ContextMenuHeaderContainer } from "containers/contextMenu/ContextMenuHeaderContainer";
import { ContextMenuGroupItemContainer } from "containers/contextMenu/ContextMenuGroupItemContainer";
import ContextMenuCss from "./css/ContextMenu.css";
import { Menu } from "@react/react-spectrum/Menu";
import { isFinite } from "utils/lodashUtils";
import { MenuDivider } from "@react/react-spectrum/Menu";
import { contextMenuTriggerType } from "dc-preview-utils/src/Constants/enums";
import { getContextMenuTriggerType } from "selectors/contextMenuSelectors";
import * as stores from "stores";
import HUDViewCss from "components/hud/HUDView.css";
import classNames from "classnames";
import { contextMenuPlacements, contextMenuVerbsWithDivider } from "constants/enums";
import { ContextMenuSection } from "components/contextMenu/ContextMenuSection";

class ContextMenu extends Component {
  constructor(props) {
    super(props);
    this.handle = null;
    this.elementToFocusAfterClose = null;
  }

  getContextMenuTopAndLeft() {
    // This function is used to get position of context menu in case of desktop
    let left, top;
    const { width, height } = this.handle.children[0].getBoundingClientRect();
    if (isFinite(this.props.xPosition)) {
      let l = this.props.xPosition;
      if (this.props.xPosition + width > window.innerWidth) {
        l = this.props.xPosition - width;
      }
      left = l;
    }
    if (isFinite(this.props.yPosition)) {
      let t = this.props.yPosition;
      if (this.props.yPosition + height > window.innerHeight) {
        t = this.props.yPosition - height;
      }
      top = t;
    }

    return { left, top };
  }

  componentPositionHelper(style) {
    if (
      !this.props.isTouchSelectionPresent &&
      (this.props.contextMenuTrigger === contextMenuTriggerType.RIGHT_CLICK ||
        this.props.contextMenuTrigger === contextMenuTriggerType.HYPERLINK_CLICK ||
        this.props.isContextMenu)
    ) {
      let { left, top } = this.getContextMenuTopAndLeft();
      if ((left || left === 0) && (top || top === 0)) {
        style.left = `${new Number(left).toString()}px`;
        style.top = `${new Number(top).toString()}px`;
        return true;
      }
    } else if (!this.props.getShouldHideContextMenu) {
      let { left, top } = this.props.getPositionInTouchDevices(this.handle, this.props.menuItems);
      if ((left || left === 0) && (top || top === 0)) {
        style.left = `${new Number(left).toString()}px`;
        style.top = `${new Number(top).toString()}px`;
        return true;
      }
    }
    return false;
  }

  componentDidUpdate() {
    this.handle.focus();
  }

  updateStyle() {
    let style = { position: "fixed" };
    if (this.handle === null || this.componentPositionHelper(style) === false) {
      style.left = "-9999px";
      style.top = "0px";
    }
    return style;
  }

  componentDidMount() {
    // Store element currently in focus (trigger element)
    this.elementToFocusAfterClose = document.activeElement;
    this.forceUpdate();
    this.handle.focus();
    if (this.handle) {
      this.props.setContextMenuHandle(this.handle);
    }
  }

  componentWillUnmount() {
    // Restore focus to trigger element
    if (
      this.elementToFocusAfterClose &&
      this.elementToFocusAfterClose.getAttribute("tabIndex") !== "-1" &&
      this.handle.contains(document.activeElement)
    )
      this.elementToFocusAfterClose.focus();
  }

  separateContextMenusByPlacement(menuItems) {
    const contextMenusByPlacement = {
      preHeaderMenuItems: [],
      headerMenuItems: [],
      bodyMenuItems: []
    };
    menuItems.forEach(menuItem => {
      if (menuItem.placement === contextMenuPlacements.CONTEXT_MENU_PRE_HEADER) {
        contextMenusByPlacement.preHeaderMenuItems.push(menuItem);
      }
      if (menuItem.placement === contextMenuPlacements.CONTEXT_MENU_HEADER) {
        contextMenusByPlacement.headerMenuItems.push(menuItem);
      }
      if (menuItem.placement === contextMenuPlacements.CONTEXT_MENU_BODY) {
        contextMenusByPlacement.bodyMenuItems.push(menuItem);
      }
    });
    return contextMenusByPlacement;
  }

  addHUDCss(className) {
    if (getContextMenuTriggerType(stores) === contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK) {
      if (this.props.showHUD) {
        className = classNames(className, HUDViewCss.HUDViewShow);
      } else {
        className = classNames(className, HUDViewCss.HUDViewHide);
      }
    }
    return className;
  }

  renderContextMenu() {
    let menuItems = [];
    let totalMenuItems = this.props.menuItems.length;
    if (getContextMenuTriggerType(stores) !== contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK) {
      totalMenuItems -= 1;
    }
    for (let i = 0; i < totalMenuItems; i++) {
      if (this.props.menuItems[i].title === "separator") {
        menuItems.push(<MenuDivider key={i} />);
      } else {
        menuItems.push(<ContextMenuItemContainer key={i} menuItem={this.props.menuItems[i]} />);
      }
    }

    let style = this.updateStyle();

    let className = ContextMenuCss.ContextMenu;
    className = this.addHUDCss(className);

    return (
      <div
        className={className}
        style={style}
        ref={handle => {
          this.handle = handle;
        }}
      >
        <Menu style={this.props.contextMenuStyle} trapFocus={this.props.trapFocus}>
          {menuItems}
        </Menu>
      </div>
    );
  }

  renderContextualTools() {
    let components = [];
    for (let i = 0; i < this.props.menuItems.length; i++) {
      components.push(this.props.menuItems[i].component);
    }

    let style = this.updateStyle();
    let rootClass =
      this.props.contextMenuTrigger === contextMenuTriggerType.COLOR_PICKER ||
      this.props.contextMenuTrigger === contextMenuTriggerType.CALENDAR
        ? ""
        : ContextMenuCss.rootDiv;
    return (
      <div
        className={rootClass}
        style={style}
        ref={handle => {
          this.handle = handle;
        }}
      >
        {components}
      </div>
    );
  }

  renderModernContextMenu() {
    let style = this.updateStyle();

    const { contextMenuTrigger } = this.props;

    if (
      contextMenuTrigger === contextMenuTriggerType.SCALE_ANNOT ||
      contextMenuTrigger === contextMenuTriggerType.COLOR_PICKER
    ) {
      const children = this.props.menuItems[0].component;
      return (
        <div
          style={style}
          ref={handle => {
            this.handle = handle;
          }}
        >
          <Menu
            className={ContextMenuCss.colorPickerWrapper}
            style={this.props.contextMenuStyle}
            trapFocus={this.props.trapFocus}
          >
            {children}
          </Menu>
        </div>
      );
    }

    if (contextMenuTrigger === contextMenuTriggerType.CALENDAR) {
      return (
        <div
          style={style}
          ref={handle => {
            this.handle = handle;
          }}
        >
          {this.props.menuItems[0].component}
        </div>
      );
    }
    let menuItems = [];
    const { preHeaderMenuItems, headerMenuItems, bodyMenuItems } = this.separateContextMenusByPlacement(
      this.props.menuItems
    );
    const noBody = bodyMenuItems.length === 0;
    if (preHeaderMenuItems.length > 0) {
      // Render any pre-header items here
      preHeaderMenuItems.forEach((menuItem, i) => {
        menuItems.push(<ContextMenuItemContainer key={i} menuItem={menuItem} />);
      });
    }
    if (headerMenuItems.length > 0) {
      menuItems.push(<ContextMenuHeaderContainer key="header" menuItems={headerMenuItems} noBody={noBody} />);
    }
    if (bodyMenuItems.length > 0) {
      bodyMenuItems.forEach((menuItem, i) => {
        if (menuItem.isSection) {
          // this is a section item.
          const isItemAtTop = i === 0;
          const isItemAtBottom = i === bodyMenuItems.length - 1;
          menuItems.push(
            <ContextMenuSection
              showTopBorder={!isItemAtTop}
              showBottomBorder={!isItemAtBottom}
              key={i}
              menuItem={menuItem}
            />
          );
        } else if (menuItem.children && menuItem.children.length) {
          // this is a group item
          menuItems.push(<ContextMenuGroupItemContainer key={i} menuItem={menuItem} />);
        } else {
          const showDivider =
            contextMenuVerbsWithDivider[menuItem.verbId] &&
            contextMenuTrigger === contextMenuTriggerType.TEXT_SELECTION;
          const showDividerInTop = showDivider && i !== 0;
          menuItems.push(<ContextMenuItemContainer key={i} menuItem={menuItem} showDividerInTop={showDividerInTop} />);
        }
      });
    }

    let className = ContextMenuCss.ContextMenu;
    className = this.addHUDCss(className);

    return (
      <div
        className={className}
        data-test-id="context-menu"
        style={style}
        ref={handle => {
          this.handle = handle;
        }}
        id="context-menu-container"
      >
        <Menu
          className={`${ContextMenuCss.ContextMenuInnerWrapper} ${noBody ? ContextMenuCss.noBody : ""}`}
          style={this.props.contextMenuStyle}
          trapFocus={this.props.trapFocus}
        >
          {menuItems}
        </Menu>
      </div>
    );
  }

  render() {
    if (this.props.useModernViewer) {
      let a = this.renderModernContextMenu();
      return a;
    }
    if (
      this.props.contextMenuTrigger !== contextMenuTriggerType.RIGHT_CLICK &&
      this.props.contextMenuTrigger !== contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK &&
      !this.props.isContextMenu
    )
      return this.renderContextualTools();
    return this.renderContextMenu();
  }
}

export { ContextMenu };
